import { useState } from 'react';
import axios from 'axios';
import { isNil } from 'lodash';

import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { GenericResponse, TemplateRequest, TemplateResponse } from '~/server_shared/openapi/data-contracts';

import type { LineOfBusiness, PolicyTemplateResponse } from '../types/policy.types';

/**
 * Type guard to validate TemplateResponse from API
 * @param data - The data to validate
 * @returns true if data is a valid TemplateResponse
 */
function isValidTemplateResponse(data: unknown): data is TemplateResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'template' in data &&
    typeof (data as Record<string, unknown>).template === 'object' &&
    (data as Record<string, unknown>).template !== null
  );
}

// Hook for managing policy templates
export const usePolicyTemplates = () => {
  const { organization } = useSysconfig() as { organization: { id: string } };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cachedTemplates, setCachedTemplates] = useState<Map<string, PolicyTemplateResponse>>(new Map());

  const getTemplate = async (request: TemplateRequest): Promise<PolicyTemplateResponse> => {
    setLoading(true);
    setError(null);

    try {
      // Create cache key from request parameters
      const cacheKey = `${request.lob}_${request.sub_organization_external_id || 'default'}`;

      // Check cache first
      if (cachedTemplates.has(cacheKey)) {
        return cachedTemplates.get(cacheKey)!;
      }

      // Build query parameters for template request
      const params = new URLSearchParams();
      params.append('lob', request.lob);
      if (!isNil(request.sub_organization_external_id)) {
        params.append('sub_organization_external_id', request.sub_organization_external_id);
      }

      // Make API call to new backend endpoint for templates with proper typing
      const response = await axios.get<TemplateResponse | GenericResponse>(
        `/sysconfig/api/v1/organizations/${organization.id}/mock_policy_server/templates?${params.toString()}`
      );

      // Check if response is an error
      if ('success' in response.data && !response.data.success) {
        const errorResponse = response.data as GenericResponse;
        throw new Error(errorResponse.message);
      }

      // Validate the response structure using type guard
      if (!isValidTemplateResponse(response.data)) {
        throw new Error('Invalid template response structure from API');
      }

      const apiResponse = response.data;

      // Convert API response to local type - keep template as generic object
      // The JsonEditor expects Record<string, unknown> anyway
      const templateResponse: PolicyTemplateResponse = {
        template: apiResponse.template as Record<string, unknown>,
        lob: request.lob,
        sub_organization_external_id: request.sub_organization_external_id,
      };

      // Cache the template response
      setCachedTemplates(new Map(cachedTemplates.set(cacheKey, templateResponse)));

      return templateResponse;
    } catch (err) {
      // For development: Return a mock template when API is not available
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        const mockTemplate: PolicyTemplateResponse = {
          lob: request.lob,
          sub_organization_external_id: request.sub_organization_external_id,
          template: {
            policy_uuid: 'template-uuid-placeholder',
            policy_number: 'TEMPLATE-{{UUID}}',
            version: 1,
            lob: request.lob,
            sub_organization_external_id: request.sub_organization_external_id,
            effective_date: '{{TODAY}}',
            expiration_date: '{{TODAY+365}}',
            policy_status: 'draft',
            policy_type: 'template',
            policy_country: 'US',
            policy_us_state: 'CA',
            coverage: {
              liability_limit: 100000,
              deductible: 500,
            },
            customer: {
              name: '{{CUSTOMER_NAME}}',
              email: '{{CUSTOMER_EMAIL}}',
              phone: '{{CUSTOMER_PHONE}}',
            },
            template_metadata: {
              template_version: '1.0',
              created_at: new Date().toISOString(),
              description: `Mock template for ${request.lob.replace('_', ' ')} policy`,
            },
          } as Record<string, unknown>,
        };

        // Cache the mock template
        const mockCacheKey = `${request.lob}_${request.sub_organization_external_id || 'default'}`;
        setCachedTemplates(new Map(cachedTemplates.set(mockCacheKey, mockTemplate)));
        return mockTemplate;
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch template';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearCache = () => {
    setCachedTemplates(new Map());
  };

  const getCachedTemplate = (lob: LineOfBusiness, subOrgId?: number): PolicyTemplateResponse | null => {
    const cacheKey = `${lob}_${subOrgId || 'default'}`;
    return cachedTemplates.get(cacheKey) || null;
  };

  return {
    getTemplate,
    clearCache,
    getCachedTemplate,
    loading,
    error,
    cacheSize: cachedTemplates.size,
  };
};
